import React, { useCallback, RefObject } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Alert, useTheme, LinearProgress } from '@mui/material';
import { CampaignProspectType, updateCampaign } from '../../api/campaigns';
import {
  useCampaignContextController,
  useSpreadsheetViewType,
  updateCurrentCampaign
} from '../../context/CampaignContext';
import { resolveCampaignHeaderBackground } from '../../theme';
import { alpha } from '@mui/material/styles';
import { PersonSearch } from '@mui/icons-material';
import { useTargetPersonaSearch } from '../../context/TargetPersonaSearchContext';
import { useSnackbar } from '../../context/SnackbarContext';
import AvatarMenu from './AvatarMenu';
import LoadingComponent from '../loading';
import CampaignBreadcrumb from '../breadcrumbs/CampaignBreadcrumb';
import CampaignActions from './CampaignActions';
import SpreadsheetViewToggle from './SpreadsheetViewToggle';
import SpreadsheetSaveIndicator from './SpreadsheetSaveIndicator';
import type { SpreadsheetRefHandle } from '../spreadsheet/Spreadsheet';

interface CampaignPageTopbarProps {
  spreadsheetRef?: RefObject<SpreadsheetRefHandle | null>;
  /** Row indices currently hidden by the sheet filter; used by export to exclude filtered rows. */
  getFilteredOutRowIds?: () => number[] | null;
}

const CampaignPageTopbar: React.FC<CampaignPageTopbarProps> = ({
  spreadsheetRef,
  getFilteredOutRowIds
}) => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const theme = useTheme();
  const spreadsheetViewType = useSpreadsheetViewType();
  const [campaignState, dispatch] = useCampaignContextController();
  const { showSnackbar } = useSnackbar();
  const {
    currentCampaign,
    currentCampaignLoading,
    canEdit,
    lockedByUserName,
    lockLoading
  } = campaignState;
  const viewType = useSpreadsheetViewType();
  const { isSearchRunning } = useTargetPersonaSearch();
  const showTargetLeadSearchBanner =
    currentCampaign?.prospect_type === CampaignProspectType.ACCOUNT &&
    viewType === 'lead' &&
    isSearchRunning;

  const handleCampaignNameChange = useCallback(
    async (newName: string) => {
      if (!campaignId || !currentCampaign || currentCampaign.name === newName) {
        return;
      }
      try {
        const updated = await updateCampaign(campaignId, { name: newName });
        updateCurrentCampaign(dispatch, updated);
        showSnackbar('Campaign name updated', 'success');
      } catch {
        showSnackbar('Failed to update campaign name', 'error');
      }
    },
    [campaignId, currentCampaign, dispatch, showSnackbar]
  );

  if (currentCampaignLoading || lockLoading) {
    return <LoadingComponent message="Fetching campaign..." />;
  }

  const headerChromeBg =
    currentCampaign != null
      ? resolveCampaignHeaderBackground(
          theme.customColors,
          currentCampaign.prospect_type,
          spreadsheetViewType
        )
      : theme.customColors.themeTertiary;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      {currentCampaign && !canEdit && lockedByUserName && (
        <Alert
          severity="info"
          sx={{
            borderRadius: 0,
            '& .MuiAlert-message': { flex: 1 }
          }}
        >
          View only. Campaign is being edited by{' '}
          <strong>{lockedByUserName}</strong>. The lock will expire after 30
          minutes of inactivity.
        </Alert>
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 0,
          pl: 2,
          pr: 1,
          backgroundColor: headerChromeBg,
          position: 'relative',
          transition: 'background-color 0.2s ease'
        }}
      >
        {/* Left Section - Breadcrumb + View toggle (account campaigns only) */}
        {currentCampaign && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: 0
            }}
          >
            <CampaignBreadcrumb
              prospectType={
                currentCampaign?.prospect_type ??
                CampaignProspectType.OPPORTUNITY
              }
              campaignName={currentCampaign?.name || ''}
              onCampaignNameChange={
                canEdit ? handleCampaignNameChange : undefined
              }
              readOnly={!canEdit}
              onTintedHeader
            />
            {currentCampaign.prospect_type === CampaignProspectType.ACCOUNT && (
              <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                <SpreadsheetViewToggle />
              </Box>
            )}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                ml:
                  currentCampaign.prospect_type === CampaignProspectType.ACCOUNT
                    ? 1.5
                    : 2
              }}
            >
              <SpreadsheetSaveIndicator />
            </Box>
          </Box>
        )}

        {/* Right Section - Import/Export buttons and Avatar Menu */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {currentCampaign && (
            <CampaignActions
              prospectType={
                currentCampaign?.prospect_type ??
                CampaignProspectType.OPPORTUNITY
              }
              canEdit={canEdit}
              spreadsheetRef={spreadsheetRef}
              getFilteredOutRowIds={getFilteredOutRowIds}
            />
          )}
          <AvatarMenu />
        </Box>
      </Box>
      {showTargetLeadSearchBanner && (
        <Box
          sx={(theme) => ({
            position: 'relative',
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor: alpha(
              theme.palette.info.main,
              theme.palette.mode === 'dark' ? 0.12 : 0.08
            ),
            '@keyframes personaSearchBannerPulse': {
              '0%, 100%': {
                backgroundColor: alpha(
                  theme.palette.info.main,
                  theme.palette.mode === 'dark' ? 0.12 : 0.08
                )
              },
              '50%': {
                backgroundColor: alpha(
                  theme.palette.info.main,
                  theme.palette.mode === 'dark' ? 0.22 : 0.16
                )
              }
            },
            animation: 'personaSearchBannerPulse 2.5s ease-in-out infinite'
          })}
        >
          <Alert
            severity="info"
            icon={
              <PersonSearch
                fontSize="inherit"
                sx={{
                  '@keyframes personaSearchIconPulse': {
                    '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                    '50%': { opacity: 0.65, transform: 'scale(0.96)' }
                  },
                  animation: 'personaSearchIconPulse 1.4s ease-in-out infinite'
                }}
              />
            }
            sx={{
              borderRadius: 0,
              py: 0.75,
              mb: 0,
              bgcolor: 'transparent',
              border: 'none',
              '& .MuiAlert-message': { flex: 1, width: '100%' }
            }}
          >
            <strong>Target lead search in progress.</strong> The sheet is
            read-only until the persona search finishes.
          </Alert>
          <LinearProgress
            color="info"
            variant="indeterminate"
            sx={{
              height: 3,
              borderRadius: 0,
              '& .MuiLinearProgress-bar': {
                transition: 'transform 0.4s linear'
              }
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default CampaignPageTopbar;
